import { Badge, Combobox, Flex, HStack, Icon, Portal, Text, useFilter, useListCollection } from '@chakra-ui/react';
import { TbShieldLock } from 'react-icons/tb';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// keeps the section of the current page (e.g. /admin/posts/12 -> /admin/posts)
// since ids in deeper paths belong to the previously selected organization
function sectionPath(pathname) {
    const segments = pathname.split('/').filter(Boolean)
    if (segments[0] === 'admin') {
        return `/${segments.slice(0, 2).join('/')}`
    }
    return `/${segments.slice(0, 1).join('/')}`
}

export function OrganizationSwitcher({ organizations, organization, ...props }) {
    const navigate = useNavigate()
    const location = useLocation()
    const { contains } = useFilter({ sensitivity: "base" })
    const items = useMemo(
        () => organizations.map(org => ({ label: org.Name, value: org.ID.toString() })),
        [organizations]
    )
    const { collection, filter } = useListCollection({
        initialItems: items,
        filter: contains,
    })

    const selectedValue = organization?.ID ? [organization.ID.toString()] : []

    function handleValueChange(e) {
        const orgID = e.value[0]
        if (!orgID || orgID === organization?.ID?.toString()) {
            return
        }
        navigate(`${sectionPath(location.pathname)}?orgID=${orgID}`)
    }

    return (
        <Flex
            colorPalette="orange"
            bg="colorPalette.subtle"
            borderWidth="1px"
            borderColor="colorPalette.muted"
            borderRadius={{ base: 0, md: "md" }}
            px="4"
            py="2"
            gap="3"
            align={{ base: "stretch", md: "center" }}
            direction={{ base: "column", md: "row" }}
            {...props}
        >
            <HStack gap="2">
                <Icon as={TbShieldLock} boxSize="5" color="colorPalette.fg" />
                <Badge variant="solid" size="md">Super User Mode</Badge>
                <Text fontSize="sm" color="colorPalette.fg" hideBelow="lg">
                    You are viewing this organization as a super user
                </Text>
            </HStack>
            <Combobox.Root
                key={organization?.ID}
                collection={collection}
                value={selectedValue}
                defaultInputValue={organization?.Name}
                onValueChange={handleValueChange}
                onInputValueChange={(e) => filter(e.inputValue)}
                onOpenChange={(e) => e.open && filter("")}
                openOnClick
                size="sm"
                width={{ base: "full", md: "xs" }}
                ms={{ md: "auto" }}
                colorPalette="gray"
            >
                <Combobox.Control>
                    <Combobox.Input placeholder="Search organizations" bg="white" />
                    <Combobox.IndicatorGroup>
                        <Combobox.Trigger />
                    </Combobox.IndicatorGroup>
                </Combobox.Control>
                <Portal>
                    <Combobox.Positioner>
                        <Combobox.Content>
                            <Combobox.Empty>No organizations found</Combobox.Empty>
                            {collection.items.map((item) => (
                                <Combobox.Item item={item} key={item.value}>
                                    {item.label}
                                    <Combobox.ItemIndicator />
                                </Combobox.Item>
                            ))}
                        </Combobox.Content>
                    </Combobox.Positioner>
                </Portal>
            </Combobox.Root>
        </Flex>
    )
}
